import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { executeAssistMigration } from 'api/assist/migration/executeAssistMigration';
import { ApiError } from 'types/api';
import {
  AssistMigrationDistributionPreview,
  AssistMigrationExecuteResult,
} from 'types/assist/assistMigration';

interface UseExecuteAssistMigrationParams {
  successCallback?: (result: AssistMigrationExecuteResult) => void;
}

interface ExecuteAssistMigrationPayload {
  businessId: number;
  distribution?: AssistMigrationDistributionPreview | null;
}

const TOAST_BY_STATUS: Record<AssistMigrationExecuteResult['status'], () => void> = {
  completed: () => toast.success('Migration completed', { duration: 4000 }),
  partial: () =>
    toast('Migration completed with issues — review the steps', { duration: 5000, icon: '⚠️' }),
  failed: () => toast.error('Migration failed — review the steps', { duration: 5000 }),
};

export const useExecuteAssistMigration = ({
  successCallback,
}: UseExecuteAssistMigrationParams = {}) => {
  return useMutation<AssistMigrationExecuteResult, ApiError, ExecuteAssistMigrationPayload>({
    mutationFn: async ({ businessId, distribution }) => {
      const { data } = await executeAssistMigration(businessId, distribution);

      return data.data;
    },
    onSuccess: (result) => {
      TOAST_BY_STATUS[result.status]();
      successCallback?.(result);
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.error(error?.response?.data?.error ?? 'An error occurred while running the migration', {
        duration: 4000,
      });
    },
  });
};
